/**
 * IGRA-32 — `/vpn/public/checkout*` API wrapper (guest tariff purchase).
 * Mirrors `giftsApi.ts`/`billingApi.ts`: serves local fixtures when
 * `VITE_IGRA_USE_MOCKS=1`, calls the real endpoints otherwise (they 404 until
 * the parallel IGRA-31 backend ships — explicit documented placeholder, not a
 * permanent mock).
 *
 * Fixtures live in this file (not `vpnFixtures.ts`) to avoid touching the
 * shared fixture file that parallel tasks edit; only the `isMocksEnabled`
 * switch is imported from there.
 *
 * Dev-only mock switches (browser console, no rebuild needed):
 *   localStorage.setItem('igra_vpn_mock_public_trial3_used', '1') // trial3 → VPN_TRIAL3_ALREADY_USED
 *   localStorage.setItem('igra_vpn_mock_public_captcha_fail', '1') // any submit → VPN_CAPTCHA_INVALID
 *   location.reload()
 * Default (both unset): submits succeed with a mock payment_url.
 */
import { httpClient } from '@/shared/api/httpClient'
import { isMocksEnabled } from '@/shared/api/mocks/vpnFixtures'
import type {
  PublicCheckoutInvite,
  PublicCheckoutOption,
  PublicCheckoutOptionsResponse,
  PublicCheckoutPayload,
  PublicCheckoutResponse,
} from '../types/publicCheckout'

export const VPN_CAPTCHA_INVALID = 'VPN_CAPTCHA_INVALID'
export const VPN_TRIAL3_ALREADY_USED = 'VPN_TRIAL3_ALREADY_USED'

const MOCK_TRIAL_USED_KEY = 'igra_vpn_mock_public_trial3_used'
const MOCK_CAPTCHA_FAIL_KEY = 'igra_vpn_mock_public_captcha_fail'
const MOCK_AMOUNT_MISMATCH_KEY = 'igra_vpn_mock_public_amount_mismatch'

/** Prices per the operator-approved grid: 350 / 990 / 1 990 ₽ + one-off
 * 3-day trial for 3 ₽. HK-IGRA.57 (PRD §3.4.1): included devices depend on the
 * purchased period — month 3 · quarter 5 · year 5 (HK-IGRA.62, operator decision
 * 2026-08-16: the year was lowered from 6 to 5); the one-off trial carries no
 * prepayment bonus. The mock must repeat what the handler returns, otherwise the
 * landing shows one number in dev and another in production.
 * IGRA-41: a guest without an invitation gets no personal price and no circle
 * facts at all — the mock mirrors that exactly.
 * IGRA-47: `per_month_price`/`savings_percent` come READY from the backend —
 * the mock carries the same numbers the real handler returns for a guest
 * without an invitation (990/3 = 330 → −6%, 1990/12 = 166 → −53%). */
const publicCheckoutOptionsFixture: PublicCheckoutOption[] = [
  {
    period: 'month',
    price: 350,
    base_price: 350,
    personal_price: null,
    device_limit: 3,
    per_month_price: 350,
    savings_percent: null,
    period_days: 30,
    renewal_price: 350,
  },
  {
    period: 'quarter',
    price: 990,
    base_price: 990,
    personal_price: null,
    device_limit: 5,
    per_month_price: 330,
    savings_percent: 6,
    period_days: 90,
    renewal_price: 350,
  },
  {
    period: 'year',
    price: 1990,
    base_price: 1990,
    personal_price: null,
    device_limit: 5,
    per_month_price: 166,
    savings_percent: 53,
    period_days: 365,
    renewal_price: 350,
  },
  {
    period: 'trial3',
    price: 3,
    base_price: 3,
    personal_price: null,
    device_limit: 3,
    per_month_price: null,
    savings_percent: null,
    period_days: 3,
    renewal_price: 350,
  },
]

/** IGRA-41 mock of an invited guest: circle of 4 → the guest becomes the 5th
 * and pays grid[5] = 239 ₽ (cheaper than the flat 249 ₽ promo). */
const MOCK_CIRCLE_GRID = [350, 309, 279, 255, 239, 225, 215, 209, 204, 199].map(
  (price, index) => ({
    members: index + 1,
    price,
  }),
)

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** Mimics the backend's `SpaceException`-style error body (same shape the
 * other vpn API mocks use) so the store's classifier exercises the exact
 * code path it will use against the real backend. */
function apiError(code: string, status = 400): Error {
  const error = new Error(code) as Error & {
    response: { status: number; data: unknown }
  }
  error.response = {
    status,
    data: { result: 'error', error_message: code },
  }
  return error
}

/**
 * HK-IGRA.53 — ЕДИНСТВЕННОЕ место, где мок считает цены гостя: и витрина, и
 * счёт обязаны брать число отсюда. Раньше `createPublicCheckout` брал цену из
 * БАЗОВОЙ фикстуры, и приглашённый, выбрав месяц за 239 ₽, получал на /buy
 * панель поправки до 350 ₽ при выключенном dev-свитче — мок демонстрировал
 * расхождение «показали одно, списали другое», которого в реальности нет.
 */
function mockPricedOptions(
  invite: PublicCheckoutInvite,
): PublicCheckoutOptionsResponse {
  const options = publicCheckoutOptionsFixture.map((option) => ({
    ...option,
  }))
  if (!invite.referral) {
    return { options, invited: false, circle: null }
  }

  // HK-IGRA.51 (PRD §3.3): промо за приглашение отменено, личная цена — это
  // цена места в круге. IGRA-45: место занимает ЛЮБОЙ пришедший по реферальной
  // ссылке, поэтому отдельной ветки «/r без круга» больше нет — прежние
  // «плоские 249 ₽» не существуют.
  // Круг приглашающего из 4 → гость становится пятым и платит grid[5] = 239 ₽.
  const personalMonth = 239

  const month = options.find((option) => option.period === 'month')
  if (month) {
    month.price = personalMonth
    month.personal_price = personalMonth
    month.per_month_price = personalMonth
  }

  // HK-IGRA.54: следующее списание у приглашённого — его личная цена месяца, и
  // она одна на все тарифы (автопродление всегда месячное). Оставить здесь
  // базовые 350 ₽ значило бы показать в согласии сумму, которой не будет.
  for (const option of options) {
    option.renewal_price = personalMonth
  }

  // IGRA-50 (PRD §3.1): квартал считается от ЛИЧНОЙ цены месяца —
  // `min(990, 239 × 3)` = 717 ₽. Мок раньше оставлял здесь 990 ₽ и показывал
  // приглашённому 330 ₽/мес при его же цене месяца 239 ₽ — то есть предлагал
  // переплатить за предоплату. Выгоды за предоплату у квартала нет по
  // построению (личная × 3 = три месяца по отдельности), поэтому бейджа тоже
  // нет: `savings_percent` при неположительной выгоде обязан быть null.
  const quarter = options.find((option) => option.period === 'quarter')
  if (quarter) {
    const personalQuarter = Math.min(quarter.base_price, personalMonth * 3)
    quarter.price = personalQuarter
    quarter.personal_price = personalQuarter
    quarter.per_month_price = personalMonth
    quarter.savings_percent = null
  }

  // IGRA-47: выгода года считается от ЕГО цены месяца — 1 − 166/239 = 31%,
  // а не от чужих для него 350 ₽ (это давало бы −53%).
  const year = options.find((option) => option.period === 'year')
  if (year) year.savings_percent = 31

  return {
    options,
    // HK-IGRA.55 — тот же явный признак, что отдаёт бэкенд.
    invited: true,
    circle: {
      members_count: 4,
      seats: 10,
      next_price_if_joined: 225,
      grid: MOCK_CIRCLE_GRID.map((t) => ({ ...t })),
    },
  }
}

/**
 * IGRA-41 — the showcase is priced for THIS guest: the inviter markers travel
 * with the request, otherwise the page would show 350 ₽ and the invoice would
 * be 239 ₽. The backend verifies the inviter and ignores a broken code (200,
 * simply no discount).
 */
export async function fetchPublicCheckoutOptions(
  invite: PublicCheckoutInvite = { referral: null, circleInvite: false },
): Promise<PublicCheckoutOptionsResponse> {
  if (isMocksEnabled()) {
    await delay(300)
    return mockPricedOptions(invite)
  }

  const params: Record<string, string> = {}
  if (invite.referral) {
    params.referral = invite.referral
    if (invite.circleInvite) params.circle_invite = '1'
  }

  const query = new URLSearchParams(params).toString()
  const response = await httpClient.get<{
    data: PublicCheckoutOptionsResponse
  }>(`/vpn/public/checkout/options${query ? `?${query}` : ''}`)
  return {
    options: response.data.options,
    invited: response.data.invited === true,
    circle: response.data.circle ?? null,
  }
}

export async function createPublicCheckout(
  payload: PublicCheckoutPayload,
): Promise<PublicCheckoutResponse> {
  if (isMocksEnabled()) {
    await delay(400)
    if (localStorage.getItem(MOCK_CAPTCHA_FAIL_KEY) === '1') {
      throw apiError(VPN_CAPTCHA_INVALID)
    }
    if (
      payload.period === 'trial3' &&
      localStorage.getItem(MOCK_TRIAL_USED_KEY) === '1'
    ) {
      throw apiError(VPN_TRIAL3_ALREADY_USED)
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)) {
      throw apiError('VALIDATION_ERROR', 422)
    }
    // IGRA-41 dev-свитч: смоделировать «email существующего аккаунта» —
    // счёт выставится на 350 ₽ вместо показанных 239 ₽, и страница обязана
    // показать поправку до перехода на оплату.
    //   localStorage.setItem('igra_vpn_mock_public_amount_mismatch', '350')
    const mismatch = Number(
      localStorage.getItem(MOCK_AMOUNT_MISMATCH_KEY) ?? '',
    )
    // HK-IGRA.53: цена берётся ТЕМ ЖЕ кодом, что рисовал витрину этому гостю
    // (с его же маркерами приглашения), иначе мок по умолчанию показывал бы
    // поправку суммы там, где реальный бэкенд её не показывает.
    const shown =
      mockPricedOptions({
        referral: payload.referral ?? null,
        circleInvite: payload.circle_invite === true,
      }).options.find((o) => o.period === payload.period)?.price ?? 0

    return {
      payment_url: `https://pay.example/mock-public-checkout-${payload.period}`,
      amount: Number.isFinite(mismatch) && mismatch > 0 ? mismatch : shown,
      // IGRA-44: правило, по которому посчитана цена счёта. В моке гость либо
      // пришёл по ссылке (место в круге), либо нет (цена как для одного) —
      // ветки `own_circle` мок не знает, у него нет аккаунтов.
      price_reason: payload.referral ? 'invite' : 'list',
      addons: 0,
      // Личная цена месяца этого аккаунта — та же, что отдала бы витрина.
      renewal_price:
        mockPricedOptions({
          referral: payload.referral ?? null,
          circleInvite: payload.circle_invite === true,
        }).options.find((o) => o.period === 'month')?.price ?? 0,
    }
  }

  const response = await httpClient.post<{ data: PublicCheckoutResponse }>(
    '/vpn/public/checkout',
    payload,
  )
  return response.data
}
